/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useMutation } from "@tanstack/react-query";
import cn from "classnames";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import VerificationInput from "react-verification-input";

import openToast from "utils/openToast";

import { confirmSetupOtpCode } from "lib/v3/loginAPI";

import { GoBackIcon } from "components/icons";
import AttentionIcon from "components/icons/AttentionIcon";
import Text from "components/ui/Text";
import { Button } from "components/ui/Button";
import Stack from "components/ui/Stack";

import styles from "../../style.module.css";

type FormData = {
  verificationCode: string;
};

interface ConfirmationCodeProps {
  setCurrentForm: (formName: string) => void;
  setConfirmationCodes: (codes: string) => void;
}

const ConfirmationCode = ({
  setCurrentForm,
  setConfirmationCodes,
}: ConfirmationCodeProps) => {
  const {
    control,
    watch,
    getValues,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      verificationCode: "",
    },
  });

  const verifyCode = useMutation(confirmSetupOtpCode);
  const verificationCode = watch("verificationCode");
  const confirmationLength = 6;
  const isCompleted = verificationCode?.length === confirmationLength;

  const onSubmit: SubmitHandler<FormData> = () => {
    const code = getValues("verificationCode");

    verifyCode.mutate(code, {
      onSuccess: () => {
        clearErrors("verificationCode");
        setConfirmationCodes(code);
        setCurrentForm("recovery_codes");
      },
      onError: (error: any) => {
        setError("verificationCode", {
          type: "manual",
          message: "Code is incorrect. Please try again.",
        });

        openToast(error.response.data.error, "error");
      },
    });
  };

  const handleComplete = () => {
    const verificationCode = getValues("verificationCode");
    if (verificationCode.length === confirmationLength) {
      handleSubmit(onSubmit)();
    }
  };

  const handleChange = (value: string) => {
    if (/^\d*$/.test(value)) {
      clearErrors("verificationCode");
      setValue("verificationCode", value);
    }
  };

  const isDisabled = !isCompleted || verifyCode.isLoading;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col w-full max-w-[545px] items-center"
    >
      <Stack className="space-y-12">
        <Stack className="space-y-8">
          <Stack className="gap-1">
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentForm("qr_code")}>
                <GoBackIcon fill="#fff" width="20" height="20" />
              </button>
              <h3 className="text-white text-lg md:text-xl font-bold">
                Enter Confirmation Code
              </h3>
            </div>

            <Text className="text-sm text-[#9A9A9A]">
              Once the authentication app generates a confirmation code, enter
              it below.
            </Text>
          </Stack>

          <div className="flex flex-col items-center justify-center gap-2">
            <Controller
              name="verificationCode"
              control={control}
              render={({ field }) => (
                <VerificationInput
                  {...field}
                  placeholder="-"
                  length={confirmationLength}
                  onChange={(value) => handleChange(value)}
                  onComplete={handleComplete}
                  classNames={{
                    container: "w-[260px] md:w-[300px]",
                    characterSelected: cn(
                      "border border-solid bg-transparent text-s align-middle text-center outline-none",
                      errors.verificationCode
                        ? "border-[#E46D64] text-[#E46D64]"
                        : "border-[#535353] text-white",
                    ),
                    characterInactive: cn(
                      "border border-solid bg-transparent text-s align-middle text-center",
                      errors.verificationCode
                        ? "border-[#E46D64] text-[#E46D64]"
                        : "border-[#535353] text-[#535353]",
                    ),
                    characterFilled: cn(
                      "border border-solid bg-transparent align-middle text-center",
                      errors.verificationCode
                        ? "border-[#E46D64] text-[#E46D64]"
                        : "border-[#535353] text-white",
                    ),
                    character: cn(
                      "bg-transparent border border-solid",
                      errors.verificationCode
                        ? "border-[#E46D64] text-[#E46D64]"
                        : "border-[#535353] text-[#535353]",
                    ),
                  }}
                />
              )}
            />

            {errors.verificationCode && (
              <Text className="w-full flex items-center justify-center gap-1 text-[#E46D64] text-xs text-center">
                <AttentionIcon fill="#E46D64" />
                {errors.verificationCode.message}
              </Text>
            )}
          </div>
        </Stack>

        <Button
          variant="primary"
          type="submit"
          size="xl"
          className={cn({
            [styles.disabled]: isDisabled,
          })}
          isLoading={verifyCode.isLoading}
          disabled={isDisabled}
          shouldPreventDefault={false}
          id="VerifyBtn"
        >
          Confirm
        </Button>
      </Stack>
    </form>
  );
};

export default ConfirmationCode;
